/**
 * W66.W6 — Persona triad: 3 contact cards (HR / Finance / Operations).
 * Each card: name, title, email, LinkedIn, phone, angle narrative, discussion-control angle block.
 * Objection pill colors: PRICE → orange, TIMING → yellow, AUTHORITY → red-tinted.
 */

import React from "react";

interface PersonaContact {
  slot: string;
  role: string;
  name: string;
  title: string;
  email: string;
  linkedin: string;
  phone: string;
  angle: string;
  dc_objection: string;
  dc_pre_empt: string;
  quantified_ask: string;
}

interface PersonaTriadProps {
  planData: Record<string, any>;
}

const OBJECTION_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  PRICE: {
    bg: "#FFF7ED",
    text: "#EA580C",
    border: "#FED7AA",
  },
  TIMING: {
    bg: "#FEFCE8",
    text: "#CA8A04",
    border: "#FEF08A",
  },
  AUTHORITY: {
    bg: "#FEF2F2",
    text: "#DC2626",
    border: "#FECACA",
  },
};

function PersonaCard({ contact }: { contact: PersonaContact }) {
  const objStyle = OBJECTION_STYLES[contact.dc_objection?.toUpperCase()] ?? {
    bg: "#F9FAFB",
    text: "#374151",
    border: "#E5E7EB",
  };

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: 10,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Slot header */}
      <div
        style={{
          background: "#111827",
          color: "#FFFFFF",
          padding: "8px 16px",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {contact.slot} / {contact.role === "primary" ? "Primary" : contact.role}
      </div>

      <div style={{ padding: 16 }}>
        {/* Contact info */}
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "#111827",
              marginBottom: 2,
            }}
          >
            {contact.name}
          </div>
          <div style={{ fontSize: 13, color: "#374151", marginBottom: 8 }}>
            {contact.title}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {contact.email && (
              <a
                href={`mailto:${contact.email}`}
                style={{ fontSize: 12, color: "#0B0B0F", textDecoration: "none" }}
              >
                {contact.email}
              </a>
            )}
            {contact.linkedin && (
              <a
                href={`https://${contact.linkedin.replace(/^https?:\/\//, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 12, color: "#0B0B0F", textDecoration: "none" }}
              >
                {contact.linkedin}
              </a>
            )}
            {contact.phone && (
              <span style={{ fontSize: 12, color: "#374151" }}>{contact.phone}</span>
            )}
          </div>
        </div>

        <hr style={{ border: "none", borderTop: "1px solid #E5E7EB", margin: "12px 0" }} />

        {/* Angle narrative */}
        {contact.angle && (
          <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, margin: "0 0 12px" }}>
            {contact.angle}
          </p>
        )}

        {/* Discussion control block */}
        {contact.dc_objection && (
          <div
            style={{
              background: objStyle.bg,
              border: `1px solid ${objStyle.border}`,
              borderLeft: `3px solid ${objStyle.text}`,
              borderRadius: 6,
              padding: "10px 12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "#6B7280",
                }}
              >
                Discussion Control — Objection Raised
              </span>
              <span
                style={{
                  background: objStyle.text,
                  color: "#FFFFFF",
                  borderRadius: 3,
                  padding: "2px 8px",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                }}
              >
                {contact.dc_objection}
              </span>
            </div>
            {contact.dc_pre_empt && (
              <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.5, margin: 0 }}>
                {contact.dc_pre_empt}
              </p>
            )}
            {contact.quantified_ask && (
              <p
                style={{
                  fontSize: 12,
                  color: objStyle.text,
                  fontWeight: 600,
                  margin: "6px 0 0",
                  fontStyle: "italic",
                }}
              >
                {contact.quantified_ask}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PersonaTriad({ planData }: PersonaTriadProps) {
  const contacts: PersonaContact[] = planData.persona_triad ?? [];

  if (contacts.length === 0) return null;

  return (
    <section style={{ marginBottom: 28 }}>
      <h2
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "#374151",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          margin: "0 0 10px",
          borderBottom: "1px solid #E5E7EB",
          paddingBottom: 6,
        }}
      >
        Persona Triad
      </h2>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 16,
        }}
      >
        {contacts.map((c, i) => (
          <PersonaCard key={i} contact={c} />
        ))}
      </div>
    </section>
  );
}
